import { PortalError } from '@app/utils/error/portal-error';
import { Logger } from '@app/utils/logger';
import * as log from 'loglevel';
import {
  combineLatest,
  isObservable,
  MonoTypeOperatorFunction,
  Observable,
  of,
  pipe,
  throwError,
} from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

export function arrayNotEmpty<T extends Array<T>>() {
  return filter<T>((_: T) => _.length > 0);
}

/**
 * Takes an array of observables and returns an observable that contains a reduced value (by default the reducer is the "or" function)
 * @param observables Array of boolean observables
 * @param f Reducer function (by default the or function)
 */
export function reduceBooleans$<T extends boolean>(observables: Array<Observable<T>>, f = (a, b) => a || b) {
  return combineLatest(observables).pipe(map((values) => values.reduce(f)));
}

/**
 * Takes observables of objects and returns an observable that contains a reduced value (the reducer is the "merge" function)
 * @param observables Some observables which values will be flattened into a combined object
 * @param f Function that reduces two objects
 */
export function reduceObjects$<T extends object>(
  observables: Array<Observable<T>>,
  f: (a, b) => T = (a, b) => ({ ...a, ...b })
): Observable<T> {
  return combineLatest(observables).pipe(
    map((values) => {
      let returnValue: T = {} as T;

      for (let value of values) {
        returnValue = f(returnValue, value);
      }

      return returnValue;
    })
  );
}

export function logValues<T>(logger: log.Logger = Logger) {
  return tap<T>((value) => {
    logger.debug(value);
  });
}

export function warnValues<T>(logger: log.Logger = Logger) {
  return tap<T>((value) => {
    logger.warn(value);
  });
}

export function isNot<T>(o: T) {
  return filter((value: T) => value !== o);
}

export function isTrue<T extends boolean>() {
  return filter((value: boolean) => value === true);
}

export const notUndefined = filter((_) => _ !== undefined);

export function notEmpty<T extends Array<T>>() {
  return pipe(notUndefinedF(), arrayNotEmpty());
}

export function notEmptyF<T extends Array<T>>() {
  return filter((value: T) => value !== undefined && value.length > 0);
}

export function notNullish<T>(): MonoTypeOperatorFunction<T> {
  return filter<T>((value) => value != null);
}

export function notUndefinedF<T>() {
  return filter((value: T) => value !== undefined);
}

/**
 * Wrap an error into a PortalError, so it is easy to find where the error originated.
 * @param id Convention: `${context}:${function}`
 * @param i18n optional translation key
 */
export function wrapError<T>(id: string, i18n?: string) {
  return catchError<T, Observable<T>>((cause: Error) => {
    return throwError({
      cause,
      id,
      i18n,
    } as PortalError);
  });
}

/**
 * Filter the given observable by using another observable that returns a boolean.
 * @param filter$ Observable that returns a boolean.
 */
export function filter$<T>(filter$: (value: T) => Observable<boolean>): MonoTypeOperatorFunction<T> {
  const EMPTY_VALUE = Symbol();

  return (source) => {
    return source.pipe(
      switchMap((value) => filter$(value).pipe(map((result) => (result ? value : EMPTY_VALUE)))),
      filter((value) => value !== EMPTY_VALUE)
    ) as Observable<T>;
  };
}

type MaybeObservable<T> = T | Observable<T>;

export function getObservable<T>(value: MaybeObservable<T>): Observable<T> {
  return isObservable(value) ? value : of(value);
}
